import { useState } from "react";
import { MapPinIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { buscarPorTexto } from "./lib/geocodificacion.js";

/**
 * Avisa cuando la direccion escrita ya no corresponde al punto del mapa.
 *
 * EL PROBLEMA QUE RESUELVE: la ciudad que clasifica y filtra el reporte sale de
 * las coordenadas del pin, no del texto. Quien escribia "Manizales, Caldas" sin
 * mover el marcador publicaba un reporte guardado como Pereira — invisible para
 * quien buscara Manizales. La contradiccion era silenciosa: nada en pantalla
 * decia que el texto no manda.
 *
 * Ahora, en cuanto el texto se separa del que produjo el geocodificador, se
 * muestra este aviso con la ciudad que REALMENTE se va a guardar y un boton
 * para llevar el pin a la direccion escrita.
 *
 * Por que no se mueve el pin solo al escribir: el usuario suele afinar el texto
 * ("frente a la panaderia") sin querer cambiar el lugar. Mover el marcador por
 * su cuenta seria peor que avisarle.
 */
function AvisoDireccionDesalineada({ textoActual, textoGeocodificado, ciudad, onUbicar }) {
  const [estado, setEstado] = useState("inactivo");
  const [error, setError] = useState("");

  const normalizar = (v) => (v ?? "").trim().toLowerCase();
  const divergente =
    normalizar(textoActual).length >= 4 &&
    normalizar(textoGeocodificado).length > 0 &&
    normalizar(textoActual) !== normalizar(textoGeocodificado);

  if (!divergente) return null;

  async function ubicar() {
    setEstado("buscando");
    setError("");
    try {
      const encontrada = await buscarPorTexto(textoActual);
      if (!encontrada) {
        setError("No encontramos esa dirección. Marca el punto en el mapa.");
        setEstado("inactivo");
        return;
      }
      onUbicar(encontrada);
      setEstado("inactivo");
    } catch {
      setError("No pudimos buscar la dirección. Marca el punto en el mapa.");
      setEstado("inactivo");
    }
  }

  return (
    <aside className="direccion-desalineada" role="status">
      <WarningCircleIcon weight="fill" />
      <div>
        <strong>El punto del mapa no coincide con lo que escribiste</strong>
        <p>
          El reporte se guardará en <b>{ciudad || "una ubicación sin ciudad"}</b>,
          según el marcador. Si la dirección que escribiste es la correcta,
          ubícala en el mapa.
        </p>
        {error && <p className="direccion-desalineada__error">{error}</p>}
        <button type="button" onClick={ubicar} disabled={estado === "buscando"}>
          <MapPinIcon weight="bold" />
          {estado === "buscando" ? "Buscando…" : "Ubicar esta dirección en el mapa"}
        </button>
      </div>
    </aside>
  );
}

export default AvisoDireccionDesalineada;
